import { useState, useEffect } from 'react';
import {
  loadXAPIConfig,
  saveXAPIConfig,
  type XAPIConfig,
  DEFAULT_XAPI_CONFIG
} from '../lib/xapi';
import styles from './CorporatePage.module.css';

export function CorporatePage() {
  const [config, setConfig] = useState<XAPIConfig>(DEFAULT_XAPI_CONFIG);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [saved, setSaved] = useState(false);

  // Load config on mount
  useEffect(() => {
    setConfig(loadXAPIConfig());
  }, []);

  const handleSave = () => {
    setSaving(true);
    saveXAPIConfig(config);
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);

    try {
      // Create a temporary client with current config
      const client = new (await import('../lib/xapi')).XAPIClient(config);

      if (!client.isEnabled()) {
        setTestResult({ success: false, message: 'xAPI is disabled or not configured' });
        return;
      }

      // Send a test statement
      const actor = client.createActor('test-user', 'Test User', 'test@example.com');
      const statement = {
        actor,
        verb: {
          id: 'http://adlnet.gov/expapi/verbs/experienced',
          display: { 'en-US': 'experienced' }
        },
        object: {
          id: 'https://balanced-arena.com/test/connection',
          objectType: 'Activity' as const,
          definition: {
            name: { 'en-US': 'Connection Test' },
            description: { 'en-US': 'Testing LRS connection from BalancedArena' }
          }
        }
      };

      const success = await client.sendStatement(statement);

      if (success) {
        setTestResult({ success: true, message: 'Successfully connected to LRS!' });
      } else {
        setTestResult({ success: false, message: 'Failed to send test statement. Check your credentials.' });
      }
    } catch (error) {
      setTestResult({
        success: false,
        message: `Connection error: ${error instanceof Error ? error.message : 'Unknown error'}`
      });
    } finally {
      setTesting(false);
    }
  };

  const updateConfig = <K extends keyof XAPIConfig>(key: K, value: XAPIConfig[K]) => {
    setConfig(prev => ({ ...prev, [key]: value }));
  };

  return (
    <div className={styles.container}>
      <h1 className={styles.pageTitle}>Corporate Settings</h1>
      <p className={styles.pageDescription}>
        Configure xAPI (Tin Can) integration for Learning Management System tracking.
      </p>

      {/* xAPI Configuration */}
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>xAPI / Tin Can Integration</h2>
          <label className={styles.toggle}>
            <input
              type="checkbox"
              checked={config.enabled}
              onChange={(e) => updateConfig('enabled', e.target.checked)}
            />
            <span className={styles.toggleSlider}></span>
            <span className={styles.toggleLabel}>{config.enabled ? 'Enabled' : 'Disabled'}</span>
          </label>
        </div>

        <div className={styles.fields}>
          <div className={styles.field}>
            <label className={styles.label}>LRS Endpoint URL</label>
            <input
              type="url"
              value={config.endpoint}
              onChange={(e) => updateConfig('endpoint', e.target.value)}
              className={styles.input}
              placeholder="https://lrs.example.com/xapi"
              disabled={!config.enabled}
            />
            <span className={styles.hint}>The xAPI endpoint URL of your Learning Record Store</span>
          </div>

          <div className={styles.fieldRow}>
            <div className={styles.field}>
              <label className={styles.label}>Username / Key</label>
              <input
                type="text"
                value={config.username}
                onChange={(e) => updateConfig('username', e.target.value)}
                className={styles.input}
                placeholder="API Key or Username"
                disabled={!config.enabled}
              />
            </div>
            <div className={styles.field}>
              <label className={styles.label}>Password / Secret</label>
              <input
                type="password"
                value={config.password}
                onChange={(e) => updateConfig('password', e.target.value)}
                className={styles.input}
                placeholder="API Secret or Password"
                disabled={!config.enabled}
              />
            </div>
          </div>

          <div className={styles.testConnection}>
            <button
              onClick={handleTest}
              className={styles.testButton}
              disabled={!config.enabled || testing || !config.endpoint}
            >
              {testing ? 'Testing...' : 'Test Connection'}
            </button>
            {testResult && (
              <span className={`${styles.testResult} ${testResult.success ? styles.success : styles.error}`}>
                {testResult.message}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Organization Settings */}
      <div className={styles.section}>
        <h2 className={styles.sectionTitle}>Organization</h2>
        <div className={styles.fields}>
          <div className={styles.fieldRow}>
            <div className={styles.field}>
              <label className={styles.label}>Organization ID</label>
              <input
                type="text"
                value={config.organizationId || ''}
                onChange={(e) => updateConfig('organizationId', e.target.value)}
                className={styles.input}
                placeholder="org-123"
              />
              <span className={styles.hint}>Unique identifier for your organization</span>
            </div>
            <div className={styles.field}>
              <label className={styles.label}>Organization Name</label>
              <input
                type="text"
                value={config.organizationName || ''}
                onChange={(e) => updateConfig('organizationName', e.target.value)}
                className={styles.input}
                placeholder="Acme Corp"
              />
            </div>
          </div>
        </div>
      </div>

      {/* What gets tracked */}
      <div className={styles.section}>
        <h2 className={styles.sectionTitle}>What Gets Tracked</h2>
        <div className={styles.infoBox}>
          <p>When xAPI is enabled, the following events are sent to your LRS:</p>
          <ul className={styles.trackingList}>
            <li>
              <strong>Quiz Started</strong> - When a learner begins a quiz session
            </li>
            <li>
              <strong>Question Answered</strong> - Each question response with:
              <ul>
                <li>Success/failure status</li>
                <li>Response given</li>
                <li>Time taken</li>
                <li>Question category and difficulty</li>
              </ul>
            </li>
            <li>
              <strong>Quiz Completed</strong> - Final results including:
              <ul>
                <li>Overall score and percentage</li>
                <li>Pass/fail status (70% threshold)</li>
                <li>Total duration</li>
              </ul>
            </li>
          </ul>
        </div>
      </div>

      {/* Popular LRS Providers */}
      <div className={styles.section}>
        <h2 className={styles.sectionTitle}>Popular LRS Providers</h2>
        <div className={styles.providers}>
          <a href="https://www.watershedlrs.com/" target="_blank" rel="noopener noreferrer" className={styles.provider}>
            Watershed LRS
          </a>
          <a href="https://www.learninglocker.net/" target="_blank" rel="noopener noreferrer" className={styles.provider}>
            Learning Locker
          </a>
          <a href="https://scorm.com/scorm-solved/scorm-cloud/" target="_blank" rel="noopener noreferrer" className={styles.provider}>
            SCORM Cloud
          </a>
          <a href="https://www.yetanalytics.com/" target="_blank" rel="noopener noreferrer" className={styles.provider}>
            Yet Analytics
          </a>
          <a href="https://veracity.it/" target="_blank" rel="noopener noreferrer" className={styles.provider}>
            Veracity Learning
          </a>
        </div>
      </div>

      {/* Save Button */}
      <div className={styles.actions}>
        <button onClick={handleSave} className={styles.saveButton} disabled={saving}>
          {saving ? 'Saving...' : saved ? 'Saved!' : 'Save Settings'}
        </button>
      </div>
    </div>
  );
}
